import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRevealOnScroll } from '@/hooks/useScrollReveal';

type FaqItem = {
  id: string;
  question: string;
  paragraphs: string[];
  bullets?: string[];
};

const faqItemsTr: FaqItem[] = [
  {
    id: 'mikro-ihracat',
    question: 'Mikro ihracat nedir?',
    paragraphs: [
      "Mikro ihracat, ağırlığı 600 kg ve fatura tutarı (KDV hariç) 30.000 Euro'yu aşmayan, Elektronik Ticaret Gümrük Beyannamesi (ETGB) ile gümrük müşaviri kullanmadan, hızlı kargo firmaları aracılığıyla yapılan basitleştirilmiş ihracat türüdür.",
    ],
  },
  {
    id: 'atr',
    question: 'A.TR belgesi nedir?',
    paragraphs: [
      'Avrupa Topluluğu ve Türkiye arasındaki Serbest Ticaret anlaşmasına istinaden Avrupa Topluluğu’na dahil ülkeler ile aramızdaki ticarette eşyaların serbest dolaşıma girebilmesi için düzenlenen belgedir.',
      'Pek çok Avrupa Topluluğu ülkesi için gümrük vergileri 150 Euro üzeri değerlerde oluşur ve A.TR belgesi düzenlenmiş gönderiler bu vergiden muaf tutulur.',
    ],
  },
  {
    id: 'gtip',
    question: 'GTİP numarası nedir?',
    paragraphs: [
      "Gümrük Tarife İstatistik Pozisyonu'nun kısaltmasıdır.",
      "Ülkemizde GTİP, Gümrük Tarife Cetveli'nde 12'li koda verilen isimdir.",
    ],
    bullets: [
      'İlk 4 rakam eşyanın pozisyon numarasını gösterir.',
      "İlk 6 rakam, Dünya Gümrük Örgütü'ne üye tüm ülkelerce kullanılan Armonize Sistem Nomanklatür kodunu gösterir.",
      '7-8. rakamlar Avrupa Birliği ülkeleri tarafından kullanılan Kombine Nomanklatür kodunu gösterir.',
      '9-10. rakamlar farklı vergi uygulamaları nedeniyle açılan pozisyonları gösteren kodlardır.',
      '11-12. rakamlar ise Gümrük Tarife İstatistik (GTİP) kodlarını oluşturur.',
    ],
  },
  {
    id: 'yasakli-urunler',
    question: 'Gönderilmesi yasak ürünler nelerdir?',
    paragraphs: [
      "Ticaret Bakanlığı'nın ihracı yasak ve ön izne tabi ürünler ile ilgili güncel tebliğine göre bazı ürün grupları gönderime kapalıdır.",
      'Aşağıda sık karşılaşılan yasaklı/izinli ürün gruplarını özetledik:',
    ],
    bullets: [
      'Koruyucu maske (gaz, toz ve radyoaktif toz filtreli maskeler), koruyucu tulum, sıvı geçirmez önlük, koruyucu gözlük, tıbbi/cerrahi maske, tıbbi steril/non-steril eldiven, dezenfektan, meltblown kumaş (GTİP: 56 03), ventilatör, anestezi/ventilatör devresi, ekspirasyon valfi vb.',
      'Yanıcı, patlayıcı ve aşındırıcı nitelikli tehlikeli maddeler.',
      'Bozulabilecek biyolojik maddeler ve radyoaktif maddeler.',
      'Afyon, morfin, kokain ve esrar gibi uyuşturucu maddeler.',
      'Canlı veya cansız hayvanlar.',
      'Para, çek, senet vb. kıymetli evrak ile altın, gümüş gibi değerli taşlar ve ziynet eşyaları.',
      'Kısa sürede bozulabilecek ürünler: et, tavuk, balık, bağırsak, ham deri, mutfak yağları, yumurta, sıvı deterjan, makine yağları, plastik ve yağlı boyalar.',
      'İnce ambalaj içinde ağır, sivri, ambalajı yırtabilecek maddeler.',
      'Mermi, boş kovan, tabanca, av tüfeği ve her türlü havalı/ateşli silah.',
      'Organ ve cenaze.',
      'Tütün ve tütün mamulleri.',
      'Batarya, pil gibi patlayabilecek veya alev alabilecek ürünler.',
      'Mıknatıslı ürünler.',
      'İlaç.',
      'Bıçak benzeri kesici aletler.',
      'Telif hakkı olan markalı ürünlerin orijinal olmayan kopyaları ve taklitleri.',
      'Ülkeden çıkışı veya karşı ülkeye girişi mevzuatla yasaklanmış maddeler.',
      'Sıkıştırılmış veya sıvılaştırılmış gazlar ile basınç altında çözünen gazlar.',
      'Parfüm ve deodorant.',
      'Tohum.',
      'Tamir amaçlı yurtdışına gönderilip geri gelecek ürünler.',
      'Sanat eserleri, antikalar ve yürürlükteki mevzuatla yasaklanan yayınlar/görsel-işitsel medya.',
      'Mevzuat ile alınması, satılması, taşınması veya el değiştirmesi yasaklanan tüm maddeler.',
    ],
  },
  {
    id: 'msds',
    question: 'MSDS nedir?',
    paragraphs: [
      "MSDS (Material Safety Data Sheets), Türkçede 'Malzeme Güvenlik Bilgi Formu' olarak bilinir.",
      'Günlük pratikte SDS kısaltması da kullanılır.',
      'Bu belge; kimyasal maddelerin sağlık, yangın, reaktivite ve çevresel risklerini belirtir, güvenli kullanım, depolama, taşıma ve acil durum prosedürleri hakkında yol gösterir.',
      'Kısacası MSDS/SDS, kimyasal ürünler için bir kimlik kartı niteliğindeki resmi güvenlik belgesidir.',
    ],
  },
];

const faqItemsEn: FaqItem[] = [
  {
    id: 'mikro-ihracat',
    question: 'What is micro export?',
    paragraphs: [
      'Micro export is a simplified export model handled through ETGB (Electronic Commerce Customs Declaration) without a customs broker, for shipments up to 600 kg and invoice values up to 30,000 EUR (excluding VAT), typically via express cargo carriers.',
    ],
  },
  {
    id: 'atr',
    question: 'What is the A.TR certificate?',
    paragraphs: [
      'It is a document issued under the Free Trade framework between Türkiye and the European Community to allow goods to circulate freely in trade with EU member states.',
      'For many European Community countries, customs duties apply above 150 EUR; shipments with a valid A.TR certificate are exempt from these duties.',
    ],
  },
  {
    id: 'gtip',
    question: 'What is a GTIP number?',
    paragraphs: [
      'GTIP is the abbreviation of Customs Tariff Statistical Position.',
      "In Türkiye, GTIP refers to the 12-digit code used in the Customs Tariff Schedule.",
    ],
    bullets: [
      'The first 4 digits show the goods position number.',
      'The first 6 digits show the Harmonized System nomenclature code used by all World Customs Organization member countries.',
      'Digits 7-8 show the Combined Nomenclature code used by EU countries.',
      'Digits 9-10 show subpositions opened due to different tax practices.',
      'Digits 11-12 form the national Customs Tariff Statistical (GTIP) code.',
    ],
  },
  {
    id: 'yasakli-urunler',
    question: 'Which products are prohibited for shipment?',
    paragraphs: [
      "According to the Ministry of Trade's current notices on export-prohibited and pre-permit items, certain product groups are not accepted for shipment.",
      'Common prohibited/restricted groups are listed below:',
    ],
    bullets: [
      'Protective masks (gas/dust/radioactive dust filtered), protective coveralls, liquid-proof aprons, protective goggles, medical/surgical masks, sterile/non-sterile medical gloves, disinfectants, meltblown fabric (GTIP: 56 03), ventilators, anesthesia/ventilator circuits, expiratory valves, etc.',
      'Flammable, explosive, and corrosive hazardous materials.',
      'Perishable biological substances and radioactive materials.',
      'Narcotic substances such as opium, morphine, cocaine, and cannabis.',
      'Live or dead animals.',
      'Cash, checks, promissory notes, valuable documents, and valuables such as gold, silver, precious stones, and jewelry.',
      'Quickly perishable goods: meat, poultry, fish, intestines, raw hide, cooking oils, eggs, liquid detergents, machine oils, plastics, and oil-based paints.',
      'Heavy or sharp items in thin packaging that may tear or damage the package.',
      'Ammunition, empty cartridges, handguns, hunting rifles, and all air/firearms.',
      'Organs and human remains.',
      'Tobacco and tobacco products.',
      'Batteries/cells and similar items that may ignite or explode.',
      'Magnetized products.',
      'Medicines.',
      'Knife-like cutting tools.',
      'Unauthorized copies/counterfeits of copyrighted branded products.',
      'Any item prohibited by law or regulation for exit from the origin country or entry into the destination country.',
      'Compressed or liquefied gases, or gases dissolved under pressure.',
      'Perfume and deodorant.',
      'Seeds.',
      'Products sent abroad for repair and to be returned.',
      'Artworks, antiques, and publications/audio-visual media prohibited by applicable regulations.',
      'Any material prohibited by law from being acquired, sold, transported, or transferred.',
    ],
  },
  {
    id: 'msds',
    question: 'What is MSDS?',
    paragraphs: [
      "MSDS stands for Material Safety Data Sheets, known in Turkish as 'Malzeme Güvenlik Bilgi Formu'.",
      'In practice, the abbreviation SDS is also commonly used.',
      'This document identifies health, fire, reactivity, and environmental hazards of chemical substances and provides guidance on safe use, storage, transportation, and emergency procedures.',
      'In short, MSDS/SDS is an official safety document functioning as an identity card for chemical products.',
    ],
  },
];

export default function SssPage() {
  useRevealOnScroll();
  const { i18n } = useTranslation();
  const [openId, setOpenId] = useState(faqItemsTr[0]?.id ?? '');

  const lang = useSyncExternalStore(
    (onStoreChange) => {
      const handleChange = () => onStoreChange();
      i18n.on('languageChanged', handleChange);
      i18n.on('loaded', handleChange);
      return () => {
        i18n.off('languageChanged', handleChange);
        i18n.off('loaded', handleChange);
      };
    },
    () => (i18n.language || i18n.resolvedLanguage || 'tr').toLowerCase(),
    () => 'tr',
  );

  const isEnglish = lang.startsWith('en');
  const faqItems = useMemo(() => (isEnglish ? faqItemsEn : faqItemsTr), [isEnglish]);

  useEffect(() => {
    document.title = isEnglish ? 'FAQ | CargoNet' : 'SSS | CargoNet';
    window.scrollTo(0, 0);
  }, [isEnglish]);

  return (
    <main data-i18n-skip="true" style={{ background: 'var(--bg)', color: 'var(--text1)' }}>
      <section className="relative pt-36 pb-20 overflow-hidden" style={{ background: 'var(--bg)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(var(--dot-color) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          <div style={{ position: 'absolute', top: '-200px', right: '-150px', width: 560, height: 560, background: 'radial-gradient(circle, var(--glow2) 0%, transparent 65%)', borderRadius: '50%' }} />
          <div style={{ position: 'absolute', bottom: 0, left: '-20%', right: '-20%', height: '40%', backgroundImage: 'linear-gradient(var(--grid-color) 1px, transparent 1px), linear-gradient(90deg, var(--grid-color) 1px, transparent 1px)', backgroundSize: '50px 50px', transform: 'perspective(600px) rotateX(62deg)', transformOrigin: 'bottom', maskImage: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 80%)', WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 80%)' }} />
        </div>

        <div className="relative max-w-6xl mx-auto px-5 lg:px-8 text-center">
          <div className="label inline-flex">
            <div className="label-dot" />
            {isEnglish ? 'Frequently Asked Questions' : 'Sık Sorulan Sorular'}
          </div>
          <h1
            className="h-font leading-[1.0] mb-5"
            style={{ fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 'clamp(40px,5vw,68px)', color: 'var(--text1)' }}
          >
            {isEnglish ? 'Answers Here' : 'Cevaplar Burada'}
          </h1>
        </div>
      </section>

      <section style={{ background: 'var(--bg2)', padding: '74px 0', borderTop: '1px solid var(--border)' }}>
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="space-y-4">
            {faqItems.map((item, index) => {
              const isOpen = openId === item.id;
              return (
                <article
                  key={item.id}
                  className={`reveal d${Math.min(index + 1, 6)} rounded-2xl overflow-hidden`}
                  style={{ background: 'var(--surface)', border: `1px solid ${isOpen ? 'rgba(232,97,26,0.35)' : 'var(--border)'}` }}
                >
                  <button
                    type="button"
                    onClick={() => setOpenId(isOpen ? '' : item.id)}
                    className="w-full text-left px-6 py-5 flex items-center gap-4 cursor-pointer"
                    style={{ background: 'transparent' }}
                  >
                    <span
                      className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                      style={{ background: isOpen ? 'var(--orange)' : 'rgba(232,97,26,0.12)', border: `1px solid ${isOpen ? 'var(--orange)' : 'rgba(232,97,26,0.25)'}` }}
                    >
                      <i className={`text-sm ${isOpen ? 'ri-subtract-line text-white' : 'ri-add-line text-[#E8611A]'}`} />
                    </span>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text1)', lineHeight: 1.35 }}>
                      {item.question}
                    </h2>
                  </button>

                  {isOpen && (
                    <div className="px-6 pb-6">
                      <div style={{ height: 1, background: 'var(--border)', marginBottom: 18 }} />

                      <div className="space-y-3">
                        {item.paragraphs.map((paragraph, i) => (
                          <p key={i} style={{ color: 'var(--text2)', lineHeight: 1.85, fontSize: 15 }}>
                            {paragraph}
                          </p>
                        ))}
                      </div>

                      {!!item.bullets?.length && (
                        <ul className="mt-4 space-y-2">
                          {item.bullets.map((bullet, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <span className="w-1.5 h-1.5 rounded-full mt-2.5 flex-shrink-0" style={{ background: 'var(--orange)' }} />
                              <span style={{ color: 'var(--text2)', lineHeight: 1.75, fontSize: 15 }}>{bullet}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section style={{ background: 'var(--bg)', padding: '74px 20px', borderTop: '1px solid var(--border)' }}>
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div
            className="rounded-3xl p-10 lg:p-12 text-center relative overflow-hidden"
            style={{ background: 'var(--surface)', border: '1px solid var(--border2)' }}
          >
            <div style={{ position: 'absolute', top: '-110px', left: '50%', transform: 'translateX(-50%)', width: 420, height: 420, background: 'radial-gradient(circle, var(--glow2) 0%, transparent 65%)', borderRadius: '50%', pointerEvents: 'none' }} />
            <div className="relative">
              <h2 style={{ fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 'clamp(28px,4vw,46px)', color: 'var(--text1)', marginBottom: 14 }}>
                {isEnglish ? 'Still have questions?' : 'Sorunuz hâlâ cevaplanmadı mı?'}
              </h2>
              <p style={{ fontSize: 17, color: 'var(--text2)', maxWidth: 560, margin: '0 auto 32px', lineHeight: 1.7 }}>
                {isEnglish
                  ? 'Reach out via our contact page. Our team will provide clear and up-to-date information as soon as possible.'
                  : 'İletişim sayfasından bize yazın. Ekibimiz en kısa sürede size net ve güncel bilgiyle dönüş yapsın.'}
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/iletisim" className="btn-primary cursor-pointer">
                  {isEnglish ? 'Contact Us' : 'İletişime Geç'} <i className="ri-arrow-right-line bounce-x" />
                </Link>
                <a href="tel:+908502420822" rel="nofollow" className="btn-ghost cursor-pointer">
                  <i className="ri-phone-line" /> 0850 242 0 822
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
